import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useAddTip, useUpdateTip } from "../../hooks";
import { CreateTipPayload, UpdateTipPayload } from "../../types";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface Tip {
  id: string;
  title: string;
  quickSummary: string;
  description: string;
  utilityService: string;
  icon: string;
  isActive: boolean;
  order: number;
  helpfulCount: number;
}

interface TipDialogProps {
  tip: Tip | null;
  availableIcons: { name: string; icon: any; value: string }[];
  utilityServices: string[];
  onSave: (tip: Partial<Tip>) => void;
  onCancel: () => void;
  onRefetch?: () => void; // Add optional refetch prop
}

export const TipFormDialog = ({ 
  tip, 
  availableIcons, 
  utilityServices, 
  onSave, 
  onCancel, 
  onRefetch 
}: TipDialogProps) => {
  const { mutate: addTip, isPending: isAdding } = useAddTip();
  const { mutate: updateTip, isPending: isUpdating } = useUpdateTip();
  const remoteUtilityId = getRemoteUtilityId();
  
  const isPending = isAdding || isUpdating;

  // Create form fields configuration
  const formFields: FormField[] = [
    {
      name: "title",
      type: "text",
      label: "Title",
      required: true,
      placeholder: "Enter tip title...",
      helperText: "A clear, concise title that describes what the tip is about",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "quickSummary",
      type: "text",
      label: "Quick Summary",
      required: true,
      placeholder: "Brief summary of the tip...",
      helperText: "A short one-line summary that appears in tooltips and previews",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "description",
      type: "textarea",
      label: "Detail Summary",
      required: true,
      placeholder: "Detailed description of the tip...",
      rows: 2,
      helperText: "A more detailed explanation that provides context and additional information",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
      textarea:
    "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all ",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "utilityService",
      type: "select",
      label: "Utility Service",
      required: true,
      options: utilityServices.map(service => ({
        label: service,
        value: service
      })),
      placeholder: "-- Select Utility Service --",
      helperText: "Select the utility service this tip is most relevant to",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "icon",
      type: "select",
      label: "Select Icon",
      required: true,
      options: availableIcons.map(iconData => ({
        label: iconData.name,
        value: iconData.value
      })),
      placeholder: "-- Select Icon --",
      helperText: "Choose an icon that best represents the tip's purpose or category",
      defaultValue: "lightbulb",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    }
  ];

  // Create form configuration
  const formConfig = {
    fields: formFields,
    mode: "onBlur" as const,
  };

  // Create form using FormService
  const form = FormService.createForm(formConfig);

  // Set default values when editing
  React.useEffect(() => {
    if (tip) {
      const formData = {
        title: tip.title || "",
        quickSummary: tip.quickSummary || "",
        description: tip.description || "",
        utilityService: tip.utilityService || (utilityServices.length > 0 ? utilityServices[0] : ""),
        icon: tip.icon || "lightbulb"
      };
      FormService.populateForm(form, formData, formFields);
    }
  }, [tip, utilityServices, form]);

  function handleSubmit(data: any) {
    if (!tip) {
      // Creating new tip - transform data to API payload structure
      const payload: CreateTipPayload = {
        utility_service: data.utilityService,
        name: data.title,
        quick_summary: data.quickSummary,
        description: data.description,
        code: data.title,
        remote_utility_id: remoteUtilityId,
        icon: data.icon
      };

      addTip(payload, {
        onSuccess: () => {
          onRefetch?.(); // Refetch tips data to update the UI
          onSave(data); // Call the parent's onSave for any additional handling
        },
        onError: (error) => {
          console.error('Failed to create tip:', error);
          // Handle error (maybe show toast)
        }
      });
    } else {
      // Editing existing tip - transform data to API payload structure
      const payload: UpdateTipPayload = {
        id: tip.id,
        utility_service: data.utilityService,
        name: data.title,
        quick_summary: data.quickSummary,
        description: data.description,
        code: tip.id, 
        remote_utility_id: remoteUtilityId, 
        icon: data.icon
      };

      updateTip(payload, {
        onSuccess: () => {
          onRefetch?.(); // Refetch tips data to update the UI
          onSave(data); // Call the parent's onSave for any additional handling
        },
        onError: (error) => {
          console.error('Failed to update tip:', error);
          // Handle error (maybe show toast)
        }
      });
    }
  }

  function handleCancel() {
    form.reset();
    onCancel();
  }

  // Custom footer with cancel button
  const renderCustomFooter = () => (
    <div className="flex gap-3 pt-6 border-t border-gray-200">
      <button
        type="button"
        onClick={handleCancel}
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        disabled={isPending}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={() => form.handleSubmit(handleSubmit)()}
        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        disabled={isPending}
      >
        {isPending ? (tip ? "Updating..." : "Creating...") : tip ? "Update" : "Create"} Tip
      </button>
    </div>
  );

  return (
    <Dialog open={true} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tip ? "Edit Tip" : "Add New Tip"}</DialogTitle>
          <DialogDescription>
            {tip ? "Update the tip details below." : "Create a new helpful tip for users."}
          </DialogDescription>
        </DialogHeader>
        
        <div id="tip-dynamic-form">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            config={{
              ...formConfig,
              showSubmitButton: false, // We'll handle submit in custom footer
            }}
            renderCustomFooter={renderCustomFooter}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};